import { useSyncExternalStore } from "react";

export interface AiAction {
  tool: string;
  result: Record<string, unknown>;
}

export interface ChatMsg {
  role: "user" | "assistant" | "error";
  text: string;
  actions?: AiAction[];
}

export interface Conversation {
  id: string;
  title: string;
  messages: ChatMsg[];
  createdAt: number;
  updatedAt: number;
}

interface Store {
  conversations: Conversation[];
  /** Conversa activa. `null` = esborrany nou (encara sense missatges). */
  activeId: string | null;
}

const STORAGE_KEY = "cookmenu:ai-conversations";
/** Només es conserven les converses més recents; la resta s'esborren soles. */
const MAX_CONVERSATIONS = 10;

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Títol curt derivat del primer missatge de l'usuari. */
function deriveTitle(messages: ChatMsg[]): string {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser) return "Nova conversa";
  const t = firstUser.text.trim().replace(/\s+/g, " ");
  return t.length > 42 ? `${t.slice(0, 42)}…` : t;
}

function load(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Store>;
      if (Array.isArray(parsed.conversations)) {
        return {
          conversations: parsed.conversations,
          activeId: typeof parsed.activeId === "string" ? parsed.activeId : null,
        };
      }
    }
  } catch {
    /* localStorage no disponible o corrupte: comencem de zero */
  }
  return { conversations: [], activeId: null };
}

// --- Store extern (singleton) -------------------------------------------------
// Viu fora del cicle de vida del component perquè la conversa sobrevisqui a
// tancar la finestra i, fins i tot, una resposta que arriba després de tancar-la
// es desa igualment (la promesa escriu directament aquí, no a l'estat de React).

let state: Store = load();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota plena o storage no disponible: ho ignorem */
  }
}

function setState(next: Store) {
  state = next;
  persist();
  emit();
}

/**
 * Aplica un updater als missatges de la conversa activa. Si no n'hi ha cap
 * (esborrany nou), en crea una. Després reordena per recents i talla a
 * MAX_CONVERSATIONS perquè l'històric no s'acumuli indefinidament.
 */
function setMessages(updater: (prev: ChatMsg[]) => ChatMsg[]) {
  const now = Date.now();
  let { conversations, activeId } = state;
  let current = conversations.find((c) => c.id === activeId);

  if (!current) {
    const id = newId();
    current = { id, title: "Nova conversa", messages: [], createdAt: now, updatedAt: now };
    conversations = [current, ...conversations];
    activeId = id;
  }

  const messages = updater(current.messages);
  const updated: Conversation = {
    ...current,
    messages,
    title: deriveTitle(messages),
    updatedAt: now,
  };

  conversations = conversations
    .map((c) => (c.id === activeId ? updated : c))
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_CONVERSATIONS);

  setState({ conversations, activeId });
}

/** Inicia una conversa nova (esborrany buit). Els esborranys no es desen fins
 *  que tinguin el primer missatge, així no s'omple l'històric de converses buides. */
function startNew() {
  if (state.activeId === null) return;
  setState({ ...state, activeId: null });
}

function selectConversation(id: string) {
  if (state.activeId === id) return;
  setState({ ...state, activeId: id });
}

function deleteConversation(id: string) {
  const conversations = state.conversations.filter((c) => c.id !== id);
  const activeId = state.activeId === id ? null : state.activeId;
  setState({ conversations, activeId });
}

const conversationsStore = {
  subscribe(cb: () => void) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
  getSnapshot() {
    return state;
  },
  setMessages,
  startNew,
  selectConversation,
  deleteConversation,
};

export interface UseConversations {
  /** Totes les converses, de més recent a més antiga. */
  conversations: Conversation[];
  /** Conversa activa (o `null` si és un esborrany nou). */
  active: Conversation | null;
  /** Missatges de la conversa activa. */
  messages: ChatMsg[];
  setMessages: (updater: (prev: ChatMsg[]) => ChatMsg[]) => void;
  startNew: () => void;
  selectConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
}

export function useConversations(): UseConversations {
  const snapshot = useSyncExternalStore(
    conversationsStore.subscribe,
    conversationsStore.getSnapshot,
    conversationsStore.getSnapshot
  );

  const active = snapshot.conversations.find((c) => c.id === snapshot.activeId) ?? null;

  return {
    conversations: snapshot.conversations,
    active,
    messages: active?.messages ?? [],
    setMessages: conversationsStore.setMessages,
    startNew: conversationsStore.startNew,
    selectConversation: conversationsStore.selectConversation,
    deleteConversation: conversationsStore.deleteConversation,
  };
}
